import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Car, ShieldCheck, Check, X, FileText, ExternalLink, UserCheck, Clock,
  Calendar as CalendarIcon, DollarSign, Plus, AlertCircle, Truck, Gauge, Fuel,
  Copy, IdCard, Image as ImageIcon,
} from "lucide-react";
import { api } from "../../lib/api";
import { Skeleton } from "./_shared";
import PickupHandoffModal from "./PickupHandoffModal";
import ReturnHandoffModal from "./ReturnHandoffModal";
import InspectionOverlayModal from "./InspectionOverlayModal";

const APP_ORIGIN =
  (process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin).replace(/\/$/, "");
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RentalsTab — customer self-drive rental bookings with a top approval queue.
 * Signed rentals land here in `pending_approval` status. Managers approve/deny.
 * Includes a "+ Walk-in rental" button that opens the digital contract in
 * walk-in mode (auto-approved) on the manager's device.
 */
export default function RentalsTab() {
  const [rentals, setRentals] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [showWalkin, setShowWalkin] = useState(false);
  const [handoffRental, setHandoffRental] = useState(null);
  const [returnRental, setReturnRental] = useState(null);
  const [damageRental, setDamageRental] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, v] = await Promise.all([
        api.get("/admin/rentals"),
        api.get("/admin/vehicles"),
      ]);
      setRentals(r.data || []);
      setVehicles((v.data || []).filter((x) => ["rental", "both"].includes(x.available_for)));
    } catch (e) {
      toast.error("Could not load rentals");
      setRentals([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    try {
      const { data } = await api.post(`/admin/rentals/${r.id}/approve`);
      // Auto-copy the share URL so the manager can immediately paste it to
      // the customer. If clipboard write fails (permissions, http, etc.), we
      // still show the URL in the toast for manual copy.
      const url = data?.share_url;
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Approved — status link copied to clipboard", {
            description: url,
            duration: 6000,
          });
        } catch {
          toast.success("Rental approved — customer notified", {
            description: url,
            duration: 8000,
          });
        }
      } else {
        toast.success("Rental approved — customer notified");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
  };

  const deny = async (r) => {
    const reason = window.prompt("Reason for denial (shown to customer):", "");
    if (reason === null) return;
    try {
      const { data } = await api.post(`/admin/rentals/${r.id}/deny`, { reason: reason || null });
      const url = data?.share_url;
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Denied — status link copied to clipboard", {
            description: url,
          });
        } catch {
          toast.success("Rental denied", { description: url });
        }
      } else {
        toast.success("Rental denied");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Denial failed");
    }
  };

  if (rentals === null) return <Skeleton />;

  const pending = rentals.filter((r) => r.status === "pending_approval");
  const confirmed = rentals.filter((r) => r.status === "confirmed");
  const active = rentals.filter((r) => r.status === "active");
  const other = rentals.filter((r) => !["pending_approval", "confirmed", "active"].includes(r.status));

  return (
    <div data-testid="rentals-tab" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Car size={16} className="text-cyan" />
          <div className="font-serif text-2xl">Rentals</div>
          <span className="label-caps text-white/40 text-[10px] ml-2">
            self-drive · signed contract queue
          </span>
        </div>
        <button
          type="button"
          data-testid="rentals-walkin-btn"
          onClick={() => setShowWalkin(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan text-midnight text-xs label-caps font-medium hover:brightness-110"
        >
          <Plus size={14} /> Walk-in rental
        </button>
      </div>

      {/* Pending approval — priority section */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <div className="label-caps text-amber-300 text-[11px]">
            Awaiting approval · {pending.length}
          </div>
        </div>
        {pending.length === 0 ? (
          <div className="glass rounded-2xl p-6 text-center text-white/50 text-sm border border-white/5">
            No signed contracts awaiting approval.
          </div>
        ) : (
          <div className="grid gap-4">
            {pending.map((r) => (
              <RentalCard
                key={r.id}
                rental={r}
                highlighted
                actions={
                  <>
                    <button
                      type="button"
                      data-testid={`rental-approve-${r.id}`}
                      onClick={() => approve(r)}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs label-caps bg-lime-400 text-midnight font-semibold hover:brightness-110"
                    >
                      <Check size={13} /> Approve
                    </button>
                    <button
                      type="button"
                      data-testid={`rental-deny-${r.id}`}
                      onClick={() => deny(r)}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs label-caps border border-red-400/40 text-red-300 hover:bg-red-400/10"
                    >
                      <X size={13} /> Deny
                    </button>
                  </>
                }
              />
            ))}
          </div>
        )}
      </section>

      {/* Confirmed */}
      {confirmed.length > 0 && (
        <section>
          <div className="label-caps text-lime-300 text-[11px] mb-3">
            Confirmed · {confirmed.length}
          </div>
          <div className="grid gap-3">
            {confirmed.map((r) => (
              <RentalCard
                key={r.id}
                rental={r}
                actions={
                  <button
                    type="button"
                    data-testid={`rental-handoff-${r.id}`}
                    onClick={() => setHandoffRental(r)}
                    className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110"
                  >
                    <Truck size={13} /> Complete pickup handoff
                  </button>
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* On the road */}
      {active.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-cyan animate-pulse" />
            <div className="label-caps text-cyan text-[11px]">On the road · {active.length}</div>
          </div>
          <div className="grid gap-3">
            {active.map((r) => (
              <RentalCard
                key={r.id}
                rental={r}
                actions={
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid={`rental-damage-${r.id}`}
                      onClick={() => setDamageRental(r)}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-[11px] label-caps border border-white/15 text-white/70 hover:text-white hover:border-cyan/40"
                    >
                      Damage claim view
                    </button>
                    <button
                      type="button"
                      data-testid={`rental-return-${r.id}`}
                      onClick={() => setReturnRental(r)}
                      className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110"
                    >
                      <Truck size={13} /> Process return
                    </button>
                  </div>
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* Cancelled / denied / etc. */}
      {other.length > 0 && (
        <section>
          <div className="label-caps text-white/40 text-[11px] mb-3">
            Other · {other.length}
          </div>
          <div className="grid gap-3">
            {other.map((r) => <RentalCard key={r.id} rental={r} />)}
          </div>
        </section>
      )}

      {/* Walk-in launcher modal */}
      {showWalkin && (
        <WalkinLauncherModal
          vehicles={vehicles}
          onClose={() => setShowWalkin(false)}
        />
      )}

      {/* Pickup handoff modal */}
      {handoffRental && (
        <PickupHandoffModal
          rental={handoffRental}
          onClose={() => setHandoffRental(null)}
          onDone={load}
        />
      )}

      {/* Return handoff modal */}
      {returnRental && (
        <ReturnHandoffModal
          rental={returnRental}
          onClose={() => setReturnRental(null)}
          onDone={load}
        />
      )}

      {/* Damage-claim / inspection overlay modal — shows customer markers
          alongside staff walk-around photos for dispute resolution. */}
      {damageRental && (
        <InspectionOverlayModal
          rental={damageRental}
          onClose={() => setDamageRental(null)}
        />
      )}
    </div>
  );
}

function RentalCard({ rental: r, highlighted = false, actions = null }) {
  const statusCls = {
    pending_approval: "bg-amber-400/15 text-amber-300 border-amber-400/40",
    pending_payment:  "bg-cyan/15 text-cyan border-cyan/40",
    confirmed:        "bg-lime-400/15 text-lime-300 border-lime-400/40",
    denied:           "bg-red-500/15 text-red-300 border-red-500/40",
    cancelled:        "bg-white/10 text-white/60 border-white/20",
  }[r.status] || "bg-white/10 text-white/60 border-white/20";

  const start = r.start_at ? new Date(r.start_at) : null;
  const end = r.end_at ? new Date(r.end_at) : null;

  return (
    <div
      data-testid={`rental-card-${r.id}`}
      className={`glass rounded-2xl p-5 border ${
        highlighted ? "border-amber-400/40" : "border-white/10"
      }`}
    >
      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`label-caps text-[10px] px-2 py-0.5 rounded-full border ${statusCls}`}>
              {r.status?.replace(/_/g, " ")}
            </span>
            <span className="label-caps text-[9px] text-white/40">
              {(r.payment_method || "cash").replace(/_/g, " ")}
            </span>
            {r.walkin_by && (
              <span className="label-caps text-[9px] px-2 py-0.5 rounded-full bg-cyan/10 text-cyan border border-cyan/30">
                walk-in
              </span>
            )}
          </div>
          <div className="font-serif text-xl leading-tight">{r.vehicle_name}</div>
          <div className="text-sm text-white/70 mt-0.5">
            <UserCheck size={11} className="inline mr-1 opacity-60" />
            {r.full_name || "—"}
            <span className="text-white/40 mx-1.5">·</span>
            {r.customer_email || "no email"}
            {r.customer_phone && <> <span className="text-white/40 mx-1.5">·</span> {r.customer_phone}</>}
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-white/50 mt-2">
            {start && end && (
              <span className="inline-flex items-center gap-1">
                <CalendarIcon size={11} />
                {start.toLocaleDateString()} → {end.toLocaleDateString()}
                <span className="text-white/40">({r.days || "?"}d)</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <DollarSign size={11} /> ${(r.amount || 0).toFixed(2)}
              <span className="text-white/40">deposit ${(r.deposit || 0).toFixed(2)}</span>
            </span>
            {r.contract_signed_at && (
              <span className="inline-flex items-center gap-1 text-cyan">
                <FileText size={11} /> signed {new Date(r.contract_signed_at).toLocaleDateString()}
              </span>
            )}
          </div>
          {/* Initials + signature preview */}
          {r.contract_initials && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {["p1", "p2", "p3", "p4"].map((p) => (
                <span key={p} className="inline-flex items-center gap-1 text-[10px] label-caps text-white/60 bg-white/5 rounded-full px-2 py-0.5">
                  {p.toUpperCase()}
                  <span className="text-cyan font-mono">{r.contract_initials[p] || "—"}</span>
                </span>
              ))}
            </div>
          )}
          {r.signature_data && (
            <div className="mt-3 rounded-lg border border-white/10 bg-ink-950/60 p-2 inline-block max-w-full overflow-hidden">
              <img src={r.signature_data} alt="Signature" className="h-12 object-contain" />
            </div>
          )}
          {r.denied_reason && (
            <div className="mt-2 text-xs text-red-300 flex items-start gap-1">
              <AlertCircle size={11} className="mt-0.5 flex-shrink-0" />
              <span>Denied: {r.denied_reason}</span>
            </div>
          )}
          {r.pickup_completed_at && (
            <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lime-400/10 text-lime-300 border border-lime-400/30">
                <Truck size={10} /> Released {new Date(r.pickup_completed_at).toLocaleDateString()}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/70">
                <Gauge size={10} /> {r.pickup_mileage?.toLocaleString?.() || r.pickup_mileage} mi
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 text-white/70">
                <Fuel size={10} /> {r.pickup_fuel_level}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 justify-end items-start">
          {r.share_token && (
            <>
              <CopyStatusLinkBtn rental={r} />
              <a
                href={`/b/${r.id}?token=${r.share_token}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] label-caps border border-white/15 text-white/70 hover:bg-white/5"
              >
                <ExternalLink size={11} /> View
              </a>
            </>
          )}
          {r.contract_signed_at && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/rentals/${r.id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`rental-pdf-${r.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] label-caps border border-cyan/40 text-cyan hover:bg-cyan/10"
            >
              <FileText size={11} /> Signed PDF
            </a>
          )}
          {actions}
        </div>
      </div>

      {/* Uploaded IDs — visible for pending approvals so the manager can vet
          the renter before hitting Approve. */}
      {r.status === "pending_approval" && (
        <IdReviewStrip rental={r} />
      )}
    </div>
  );
}

function CopyStatusLinkBtn({ rental: r }) {
  const [copied, setCopied] = useState(false);
  const url = `${APP_ORIGIN}/b/${r.id}?token=${r.share_token}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Status link copied", { description: "Paste it to your customer" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: show the URL in a prompt so the manager can copy manually.
      window.prompt("Copy this status link:", url);
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={`rental-copy-link-${r.id}`}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] label-caps border transition ${
        copied
          ? "border-lime-400/60 text-lime-300 bg-lime-400/10"
          : "border-cyan/40 text-cyan hover:bg-cyan/10"
      }`}
      title={url}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? "Copied!" : "Copy status link"}
    </button>
  );
}

function IdReviewStrip({ rental: r }) {
  const ids = [
    { key: "dl_front", label: "DL front", file_id: r.dl_front_file_id },
    { key: "dl_back",  label: "DL back",  file_id: r.dl_back_file_id },
    { key: "selfie",   label: "Selfie",   file_id: r.selfie_with_dl_file_id },
    { key: "insurance", label: "Insurance", file_id: r.insurance_card_file_id },
    { key: "deposit_receipt", label: "Deposit receipt", file_id: r.deposit_receipt_file_id },
    { key: "add_front", label: "Add-driver front", file_id: r.additional_driver_dl_front_file_id },
    { key: "add_back",  label: "Add-driver back",  file_id: r.additional_driver_dl_back_file_id },
  ].filter((x) => x.file_id);
  if (ids.length === 0) return null;
  const src = (fid) => `${API_BASE}/admin/uploads/${fid}`;
  return (
    <div className="mt-4 pt-4 border-t border-white/10">
      <div className="label-caps text-[10px] text-cyan mb-2 inline-flex items-center gap-1.5">
        <IdCard size={11} /> Renter verification · {ids.length} file{ids.length === 1 ? "" : "s"}
      </div>
      <div className="flex gap-2 flex-wrap">
        {ids.map((x) => (
          <a
            key={x.key}
            href={src(x.file_id)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`rental-id-${x.key}-${r.id}`}
            className="group relative w-28 h-20 rounded-lg overflow-hidden border border-white/15 bg-ink-950 hover:border-cyan/60 transition"
            title={x.label}
          >
            <img
              src={src(x.file_id)}
              alt={x.label}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-x-0 bottom-0 px-1.5 py-0.5 bg-gradient-to-t from-black/90 to-transparent text-[9px] label-caps text-white/90 truncate flex items-center gap-1">
              <ImageIcon size={8} /> {x.label}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function WalkinLauncherModal({ vehicles, onClose }) {
  // Dedupe by vehicle name — two units of the same model are interchangeable
  // unless one has `special_features` marking it as distinct. We show one entry
  // per (name + special_features) combo, and expose the quantity in the label.
  const dedupedVehicles = React.useMemo(() => {
    const map = new Map();
    for (const v of vehicles) {
      const key = `${v.name}::${(v.special_features || "").trim()}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { ...v, _unitCount: 1 });
      } else {
        existing._unitCount += 1;
      }
    }
    return Array.from(map.values());
  }, [vehicles]);

  const [vehicleId, setVehicleId] = useState(dedupedVehicles[0]?.id || "");
  const launch = () => {
    if (!vehicleId) return toast.error("Pick a vehicle");
    // Navigate the current tab to the walk-in flow so the manager can hand the
    // device to the customer.
    window.location.href = `/book/rental/${vehicleId}?walkin=1`;
  };
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="walkin-launcher-modal"
        className="w-full max-w-md bg-[#0B1120] rounded-3xl border border-cyan/40 shadow-2xl p-6 space-y-5"
      >
        <div>
          <div className="label-caps text-cyan text-[10px] mb-1">New walk-in rental</div>
          <div className="font-serif text-2xl">Start walk-in contract</div>
          <p className="text-xs text-white/60 mt-1">
            Pick the vehicle the customer wants. You&apos;ll be taken into the digital
            rental contract so they can sign on this device.
          </p>
        </div>
        <label className="block">
          <span className="label-caps text-[10px] text-white/50 block mb-1.5">Vehicle</span>
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            data-testid="walkin-vehicle-select"
            className="w-full rounded-xl bg-ink-950/80 border border-white/10 px-3 py-2.5 text-sm text-white focus:border-cyan focus:outline-none"
          >
            {dedupedVehicles.length === 0 && <option value="">No rental vehicles configured</option>}
            {dedupedVehicles.map((v) => {
              const rate = v.daily_rate || v.hourly_rate;
              const suffix = v._unitCount > 1 ? ` · ${v._unitCount} units` : "";
              const special = (v.special_features || "").trim();
              const specialSuffix = special ? ` · ${special.slice(0, 40)}${special.length > 40 ? "…" : ""}` : "";
              return (
                <option key={v.id} value={v.id}>
                  {v.name} · ${rate}/day{suffix}{specialSuffix}
                </option>
              );
            })}
          </select>
          {(() => {
            const picked = dedupedVehicles.find((v) => v.id === vehicleId);
            if (!picked) return null;
            const special = (picked.special_features || "").trim();
            return special ? (
              <div className="mt-2 text-[11px] text-cyan/90 rounded-lg bg-cyan/5 border border-cyan/20 px-3 py-2">
                <span className="label-caps text-[9px] text-cyan block mb-0.5">Special features</span>
                {special}
              </div>
            ) : null;
          })()}
        </label>
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-full text-xs label-caps text-white/60 hover:text-white border border-white/12"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="walkin-launch-btn"
            onClick={launch}
            disabled={!vehicleId}
            className="flex-1 py-2.5 rounded-full text-xs label-caps bg-cyan text-midnight font-semibold hover:brightness-110 disabled:opacity-40"
          >
            Start contract
          </button>
        </div>
      </div>
    </div>
  );
}
